import React, { useEffect, useState } from 'react';
import {
  Bookmark,
  Crown,
  Dices,
  Flame,
  Layers,
  Moon,
  NotebookPen,
  ShieldAlert,
  Sparkles,
  Sun,
  Swords,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';
import {
  RING_TEMPT_LEVELS,
  STANDARD_TOKEN_PRESETS,
  UNDERCITY_ROOMS,
  type ClientGameView,
  type GameIntent,
  type ManaPoolColor,
  type PublicCardView,
} from '#shared';
import { CardVisual } from './CardVisual.tsx';

export type PlaytestHelperTab =
  | 'tokens'
  | 'commander'
  | 'counters'
  | 'designations'
  | 'cascade'
  | 'notes';

interface PlaytestHelpersModalProps {
  view: ClientGameView;
  initialTab?: PlaytestHelperTab;
  onDispatch: (intent: GameIntent) => void;
  onClose: () => void;
}

const MANA_STYLES: { color: ManaPoolColor; label: string; badgeClass: string }[] = [
  { color: 'W', label: 'White {W}', badgeClass: 'bg-amber-100 text-slate-950 border-amber-300' },
  { color: 'U', label: 'Blue {U}', badgeClass: 'bg-sky-500 text-slate-950 border-sky-300' },
  { color: 'B', label: 'Black {B}', badgeClass: 'bg-zinc-800 text-zinc-100 border-zinc-500' },
  { color: 'R', label: 'Red {R}', badgeClass: 'bg-rose-500 text-white border-rose-300' },
  { color: 'G', label: 'Green {G}', badgeClass: 'bg-emerald-500 text-slate-950 border-emerald-300' },
  { color: 'C', label: 'Colorless {C}', badgeClass: 'bg-slate-400 text-slate-950 border-slate-200' },
];

const PLAYER_COUNTER_DEFS: { key: string; label: string; warnAt?: number }[] = [
  { key: 'poison', label: 'Poison (10 = Lethal)', warnAt: 10 },
  { key: 'energy', label: 'Energy {E}' },
  { key: 'experience', label: 'Experience' },
  { key: 'rad', label: 'Rad Counters' },
  { key: 'storm', label: 'Storm Count' },
  { key: 'speed', label: 'Speed (1–4)' },
  { key: 'ticket', label: 'Tickets {TK}' },
];

export function PlaytestHelpersAndCountersModal({
  view,
  initialTab = 'tokens',
  onDispatch,
  onClose,
}: PlaytestHelpersModalProps) {
  const [tab, setTab] = useState<PlaytestHelperTab>(initialTab);

  // Token Builder state
  const [tokenCount, setTokenCount] = useState(1);
  const [customTokName, setCustomTokName] = useState('Construct');
  const [customTokType, setCustomTokType] = useState('Token Artifact Creature — Construct');
  const [customTokPower, setCustomTokPower] = useState('2');
  const [customTokToughness, setCustomTokToughness] = useState('2');
  const [customTokOracle, setCustomTokOracle] = useState('');
  const [customTokColors, setCustomTokColors] = useState<string[]>([]);

  // Player Counter state
  const [customPlayerCtr, setCustomPlayerCtr] = useState('blessing');

  // Cascade / Reveal Until state
  const [maxCmcInput, setMaxCmcInput] = useState<string>('3');
  const [nonLandOnly, setNonLandOnly] = useState(true);
  const [cardTypeFilter, setCardTypeFilter] = useState('');
  const [privateSearchUntil, setPrivateSearchUntil] = useState(false);
  const [bottomOrder, setBottomOrder] = useState<'random' | 'current'>('random');

  // Notes & Tray state
  const mySeat = view.viewerSeatId ? view.seats[view.viewerSeatId] : null;
  const [privateNotesDraft, setPrivateNotesDraft] = useState(mySeat?.privateNotes ?? '');
  const [sharedNotesDraft, setSharedNotesDraft] = useState(view.sharedNotes ?? '');
  const [trayCardInput, setTrayCardInput] = useState('');

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (mySeat?.privateNotes !== null && mySeat?.privateNotes !== undefined) {
      setPrivateNotesDraft(mySeat.privateNotes);
    }
  }, [mySeat?.privateNotes]);

  useEffect(() => {
    setSharedNotesDraft(view.sharedNotes);
  }, [view.sharedNotes]);

  const myRevealedCards: PublicCardView[] = view.sharedZones.revealed.filter(
    (c) => view.viewerSeatId && c.ownerSeatId === view.viewerSeatId
  );

  const allBattlefieldCards: { seatName: string; card: PublicCardView }[] = view.activeSeatOrder.flatMap(
    (sid) => {
      const s = view.seats[sid];
      if (!s) return [];
      return s.zones.battlefield
        .filter((c) => c.card !== null)
        .map((card) => ({ seatName: s.displayName, card }));
    }
  );

  // Collect visible commanders across seats for the Per-Partner Commander Matrix
  const getSeatCommanders = (sid: string): { id: string; label: string }[] => {
    const s = view.seats[sid];
    if (!s) return [];
    const visibleCmds = [
      ...s.zones.command,
      ...s.zones.battlefield,
      ...s.zones.graveyard,
      ...s.zones.exile,
    ].filter((c) => c.isCommander);

    if (visibleCmds.length > 0) {
      return visibleCmds.map((c, idx) => ({
        id: c.instanceId,
        label: c.card?.name ?? `Commander #${idx + 1}`,
      }));
    }
    if (s.deckMeta && s.deckMeta.commanderNames.length > 0) {
      return s.deckMeta.commanderNames.map((name, idx) => ({
        id: `partner_${idx + 1}`,
        label: name,
      }));
    }
    return [
      { id: 'cmd_primary', label: `${s.displayName}'s Commander` },
      { id: 'cmd_partner', label: `${s.displayName}'s Partner` },
    ];
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Playtester Tools, Counters & Designations"
      className="fixed inset-0 z-[105] bg-black/75 backdrop-blur-sm flex items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[88vh] rounded-2xl bg-slate-950 border border-slate-700/90 shadow-2xl flex flex-col overflow-hidden text-slate-100"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between bg-slate-900/70">
          <div className="flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-amber-400" />
            <h2 className="font-bold text-sm tracking-wide">
              Playtester Helpers, Tokens, Counters & Commander Matrix
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="px-3 py-2 border-b border-slate-800 bg-slate-950 flex items-center gap-1.5 overflow-x-auto text-xs shrink-0">
          {(
            [
              ['tokens', 'Tokens & Clones', Sparkles],
              ['commander', 'Cmdr Matrix & Tax', Swords],
              ['counters', 'Counters & Mana', Flame],
              ['designations', 'Designations & Ring', Crown],
              ['cascade', 'Cascade & Random', Dices],
              ['notes', 'Notes & Tray', NotebookPen],
            ] as const
          ).map(([id, label, Icon]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`px-2.5 py-1.5 rounded-lg font-bold flex items-center gap-1.5 shrink-0 border transition-colors ${
                tab === id
                  ? 'bg-amber-500 text-slate-950 border-amber-400'
                  : 'bg-slate-900 text-slate-300 border-slate-800 hover:border-slate-700'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto space-y-4 text-xs flex-1">
          {/* ============================================================== */}
          {/* TAB 1: TOKENS, CLONES & CUSTOM TOKEN BUILDER                   */}
          {/* ============================================================== */}
          {tab === 'tokens' && (
            <div className="space-y-4">
              {/* Token Quantity Bar */}
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 flex flex-wrap items-center justify-between gap-2">
                <span className="font-bold text-slate-200">Create Quantity (N):</span>
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3, 5, 10].map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => setTokenCount(q)}
                      className={`px-2.5 py-1 rounded-lg font-bold border ${
                        tokenCount === q
                          ? 'bg-emerald-500 text-slate-950 border-emerald-300'
                          : 'bg-slate-950 text-slate-300 border-slate-700'
                      }`}
                    >
                      ×{q}
                    </button>
                  ))}
                  <input
                    type="number"
                    min={1}
                    max={25}
                    value={tokenCount}
                    onChange={(e) => setTokenCount(Math.max(1, Math.min(25, Number(e.target.value) || 1)))}
                    className="w-14 px-2 py-1 rounded-lg bg-slate-950 border border-slate-700 text-center font-bold"
                  />
                </div>
              </div>

              {/* Deck-Detected Tokens */}
              {mySeat && mySeat.availableTokens.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                    Auto-Detected From Your Deck
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {mySeat.availableTokens.map((tok) => (
                      <button
                        key={tok.name}
                        type="button"
                        onClick={() => onDispatch({ type: 'CREATE_TOKEN', token: tok, count: tokenCount })}
                        className="p-2.5 rounded-xl bg-emerald-950/35 hover:bg-emerald-900/50 border border-emerald-500/40 text-left flex items-center justify-between"
                      >
                        <div>
                          <div className="font-bold text-emerald-100">{tok.name}</div>
                          <div className="text-[10px] text-emerald-300/75 truncate">{tok.typeLine}</div>
                        </div>
                        {tok.power && (
                          <span className="px-1.5 py-0.5 rounded bg-slate-950 text-[10px] font-bold">
                            {tok.power}/{tok.toughness}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Standard Presets */}
              <div className="space-y-1.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Common Tabletop Tokens (Auto-vanish on leaving battlefield)
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {STANDARD_TOKEN_PRESETS.filter((t) => t.name !== 'Copy / Clone Token').map((tok) => (
                    <button
                      key={tok.name}
                      type="button"
                      onClick={() => onDispatch({ type: 'CREATE_TOKEN', token: tok, count: tokenCount })}
                      className="p-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-left flex items-center justify-between"
                    >
                      <span className="font-semibold text-slate-100">{tok.name}</span>
                      {tok.power && (
                        <span className="text-[10px] text-slate-400 font-mono">
                          {tok.power}/{tok.toughness}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Copy Existing Permanent as Token */}
              {allBattlefieldCards.length > 0 && (
                <div className="space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-cyan-400">
                    Create Token Copy of Battlefield Permanent
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {allBattlefieldCards.map(({ seatName, card }) => (
                      <button
                        key={card.instanceId}
                        type="button"
                        onClick={() =>
                          onDispatch({
                            type: 'CREATE_TOKEN',
                            token: {
                              name: card.card?.name ?? 'Token Copy',
                              typeLine: card.card?.typeLine ?? 'Token Permanent',
                              colors: card.card?.colors ?? [],
                            },
                            count: tokenCount,
                            copyOfInstanceId: card.instanceId,
                          })
                        }
                        className="px-2.5 py-1.5 rounded-lg bg-cyan-950/40 hover:bg-cyan-900/50 border border-cyan-500/40 text-cyan-100"
                      >
                        Copy {card.card?.name} ({seatName}) ×{tokenCount}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Custom Token & Emblem Builder */}
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300">
                  Custom Token / Emblem Builder
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input
                    value={customTokName}
                    onChange={(e) => setCustomTokName(e.target.value)}
                    placeholder="Token or Emblem Name"
                    className="px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700"
                  />
                  <input
                    value={customTokType}
                    onChange={(e) => setCustomTokType(e.target.value)}
                    placeholder="Type Line (e.g. Token Creature — Angel)"
                    className="px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={customTokPower}
                    onChange={(e) => setCustomTokPower(e.target.value)}
                    placeholder="P"
                    className="w-14 px-2 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-center"
                  />
                  <span>/</span>
                  <input
                    value={customTokToughness}
                    onChange={(e) => setCustomTokToughness(e.target.value)}
                    placeholder="T"
                    className="w-14 px-2 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-center"
                  />
                  <div className="flex items-center gap-1 ml-2">
                    {(['W', 'U', 'B', 'R', 'G'] as const).map((col) => {
                      const active = customTokColors.includes(col);
                      return (
                        <button
                          key={col}
                          type="button"
                          onClick={() =>
                            setCustomTokColors((prev) =>
                              prev.includes(col) ? prev.filter((x) => x !== col) : [...prev, col]
                            )
                          }
                          className={`w-6 h-6 rounded-full font-bold text-[10px] border ${
                            active
                              ? 'bg-amber-400 text-slate-950 border-white'
                              : 'bg-slate-950 text-slate-400 border-slate-700'
                          }`}
                        >
                          {col}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <input
                  value={customTokOracle}
                  onChange={(e) => setCustomTokOracle(e.target.value)}
                  placeholder="Abilities / Oracle text (e.g. Flying, Vigilance)"
                  className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700"
                />
                <button
                  type="button"
                  onClick={() =>
                    customTokName.trim() &&
                    onDispatch({
                      type: 'CREATE_TOKEN',
                      token: {
                        name: customTokName.trim(),
                        typeLine: customTokType.trim() || 'Token Permanent',
                        power: customTokPower.trim() || undefined,
                        toughness: customTokToughness.trim() || undefined,
                        oracleText: customTokOracle.trim() || undefined,
                        colors: customTokColors,
                      },
                      count: tokenCount,
                    })
                  }
                  className="px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold"
                >
                  Create {tokenCount}× {customTokName || 'Custom Token'}
                </button>
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* TAB 2: PER-PARTNER COMMANDER DAMAGE MATRIX & TAX               */}
          {/* ============================================================== */}
          {tab === 'commander' && (
            <div className="space-y-4">
              {/* My Commander Tax Stepper */}
              {mySeat && (
                <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300">
                    My Commander Cast Tax (+2 Mana per Cast)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {getSeatCommanders(mySeat.seatId).map((cmd) => {
                      const casts = mySeat.commanderTax[cmd.id] ?? 0;
                      return (
                        <div
                          key={cmd.id}
                          className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 flex items-center gap-3"
                        >
                          <div>
                            <div className="font-bold text-slate-100">{cmd.label}</div>
                            <div className="text-[10px] text-amber-300 font-mono">
                              Tax: +{casts * 2} generic ({casts} cast{casts === 1 ? '' : 's'})
                            </div>
                          </div>
                          <div className="flex items-center rounded-lg bg-slate-900 border border-slate-700 overflow-hidden">
                            <button
                              type="button"
                              onClick={() =>
                                onDispatch({
                                  type: 'ADJUST_COMMANDER_TAX',
                                  commanderInstanceId: cmd.id,
                                  delta: -1,
                                })
                              }
                              className="px-2.5 py-1 hover:bg-slate-800 text-rose-300 font-bold"
                            >
                              −
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                onDispatch({
                                  type: 'ADJUST_COMMANDER_TAX',
                                  commanderInstanceId: cmd.id,
                                  delta: 1,
                                })
                              }
                              className="px-2.5 py-1 hover:bg-slate-800 text-emerald-300 font-bold"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Full Per-Partner Commander Damage Matrix */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Per-Partner Commander Damage Taken {mySeat ? `by ${mySeat.displayName}` : ''} (21 = Lethal)
                  </span>
                </div>
                {mySeat ? (
                  <div className="space-y-2">
                    {view.seatOrder
                      .filter((sid) => view.seats[sid] && view.seats[sid].publicId !== null)
                      .map((sourceSid) => {
                        const srcSeat = view.seats[sourceSid];
                        const cmds = getSeatCommanders(sourceSid);
                        return (
                          <div
                            key={sourceSid}
                            className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2"
                          >
                            <div className="flex items-center gap-2 font-bold text-slate-200">
                              <span
                                className="w-2.5 h-2.5 rounded-full"
                                style={{ backgroundColor: srcSeat.profileColor }}
                              />
                              <span>
                                From {srcSeat.displayName}
                                {sourceSid === mySeat.seatId ? ' (Self / Control-Changed)' : ''}
                              </span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {cmds.map((cmd) => {
                                const key = `${sourceSid}:${cmd.id}`;
                                const dmg = mySeat.commanderDamage[key] ?? 0;
                                const lethal = dmg >= 21;
                                return (
                                  <div
                                    key={key}
                                    className={`p-2.5 rounded-xl border flex items-center justify-between gap-2 ${
                                      lethal
                                        ? 'bg-rose-950/75 border-rose-500 text-rose-100'
                                        : 'bg-slate-950 border-slate-800'
                                    }`}
                                  >
                                    <div className="min-w-0">
                                      <div className="font-semibold truncate">{cmd.label}</div>
                                      <div className="text-[10px] font-mono flex items-center gap-1.5">
                                        <span className={lethal ? 'text-rose-300 font-extrabold' : 'text-amber-300'}>
                                          {dmg} / 21 DMG
                                        </span>
                                        {lethal && (
                                          <span className="px-1.5 rounded bg-rose-600 text-white font-extrabold">
                                            21+ LETHAL
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      {[-5, -1, 1, 5].map((d) => (
                                        <button
                                          key={d}
                                          type="button"
                                          onClick={() =>
                                            onDispatch({
                                              type: 'ADJUST_COMMANDER_DAMAGE',
                                              targetSeatId: mySeat.seatId,
                                              sourceSeatId: sourceSid,
                                              commanderInstanceId: cmd.id,
                                              delta: d,
                                            })
                                          }
                                          className={`px-2 py-1 rounded font-bold border ${
                                            d > 0
                                              ? 'bg-slate-900 hover:bg-rose-900/60 border-slate-700 text-rose-200'
                                              : 'bg-slate-900 hover:bg-slate-800 border-slate-700 text-slate-300'
                                          }`}
                                        >
                                          {d > 0 ? `+${d}` : d}
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  <p className="text-slate-400">Sit in a seat to adjust Commander damage.</p>
                )}
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* TAB 3: FLOATING MANA POOL & PLAYER COUNTERS                    */}
          {/* ============================================================== */}
          {tab === 'counters' && mySeat && (
            <div className="space-y-4">
              {/* Floating Mana Pool */}
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2.5">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">
                    Floating Mana Pool
                  </span>
                  <button
                    type="button"
                    onClick={() => onDispatch({ type: 'CLEAR_MANA_POOL' })}
                    className="px-2.5 py-1 rounded-lg bg-rose-950/80 hover:bg-rose-900 border border-rose-500/50 text-rose-200 font-bold text-[10px]"
                  >
                    Clear Pool
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {MANA_STYLES.map(({ color, label, badgeClass }) => {
                    const qty = mySeat.manaPool[color] ?? 0;
                    return (
                      <div
                        key={color}
                        className="p-2 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-6 h-6 rounded-full border font-extrabold text-[11px] flex items-center justify-center ${badgeClass}`}
                          >
                            {color}
                          </span>
                          <div>
                            <div className="text-[10px] text-slate-400">{label}</div>
                            <div className="font-mono font-extrabold text-sm">{qty}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => onDispatch({ type: 'ADJUST_MANA_POOL', color, delta: -1 })}
                            className="px-2 py-1 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 font-bold"
                          >
                            −
                          </button>
                          <button
                            type="button"
                            onClick={() => onDispatch({ type: 'ADJUST_MANA_POOL', color, delta: 1 })}
                            className="px-2 py-1 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 font-bold text-emerald-300"
                          >
                            +1
                          </button>
                          <button
                            type="button"
                            onClick={() => onDispatch({ type: 'ADJUST_MANA_POOL', color, delta: 3 })}
                            className="px-1.5 py-1 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 font-bold text-amber-300 text-[10px]"
                          >
                            +3
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Player Counters */}
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Player Counters (Poison, Energy, Experience, Rad, Storm, Speed, Tickets)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {PLAYER_COUNTER_DEFS.map(({ key, label, warnAt }) => {
                    const val = mySeat.playerCounters[key] ?? 0;
                    const danger = warnAt !== undefined && val >= warnAt;
                    return (
                      <div
                        key={key}
                        className={`p-2.5 rounded-xl border flex items-center justify-between ${
                          danger
                            ? 'bg-rose-950/75 border-rose-500 text-rose-100'
                            : 'bg-slate-950 border-slate-800'
                        }`}
                      >
                        <div>
                          <div className="font-bold">{label}</div>
                          <div className="font-mono text-amber-300">
                            {val} {danger ? '· LETHAL THRESHOLD!' : ''}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              onDispatch({ type: 'ADJUST_PLAYER_COUNTER', counter: key, delta: -1 })
                            }
                            className="px-2.5 py-1 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 font-bold"
                          >
                            −
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              onDispatch({ type: 'ADJUST_PLAYER_COUNTER', counter: key, delta: 1 })
                            }
                            className="px-2.5 py-1 rounded bg-slate-900 hover:bg-slate-800 border border-slate-700 font-bold text-emerald-300"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Custom Player Counter */}
                <div className="flex items-center gap-2 pt-1">
                  <input
                    value={customPlayerCtr}
                    onChange={(e) => setCustomPlayerCtr(e.target.value)}
                    placeholder="Custom player counter..."
                    className="px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      customPlayerCtr.trim() &&
                      onDispatch({
                        type: 'ADJUST_PLAYER_COUNTER',
                        counter: customPlayerCtr.trim(),
                        delta: 1,
                      })
                    }
                    className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-bold"
                  >
                    +1 Counter
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* TAB 4: TABLE DESIGNATIONS, UNDERCITY & THE RING                */}
          {/* ============================================================== */}
          {tab === 'designations' && (
            <div className="space-y-4">
              {/* Core Designations Bar */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() =>
                    onDispatch({
                      type: 'SET_DESIGNATION',
                      designation: 'monarch',
                      seatId: view.viewerSeatId,
                    })
                  }
                  className={`p-3 rounded-xl border text-left space-y-1 ${
                    view.designations.monarchSeatId === view.viewerSeatId && view.viewerSeatId
                      ? 'bg-amber-500/20 border-amber-400 text-amber-100'
                      : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="font-bold flex items-center gap-1.5">
                    <Crown className="w-4 h-4 text-amber-400" />
                    <span>Become Monarch</span>
                  </div>
                  <div className="text-[10px] text-slate-400">
                    Current:{' '}
                    {view.designations.monarchSeatId
                      ? view.seats[view.designations.monarchSeatId]?.displayName
                      : 'Unclaimed'}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    onDispatch({
                      type: 'SET_DESIGNATION',
                      designation: 'initiative',
                      seatId: view.viewerSeatId,
                    })
                  }
                  className={`p-3 rounded-xl border text-left space-y-1 ${
                    view.designations.initiativeSeatId === view.viewerSeatId && view.viewerSeatId
                      ? 'bg-emerald-500/20 border-emerald-400 text-emerald-100'
                      : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="font-bold flex items-center gap-1.5">
                    <ShieldAlert className="w-4 h-4 text-emerald-400" />
                    <span>Take Initiative</span>
                  </div>
                  <div className="text-[10px] text-slate-400">
                    Current:{' '}
                    {view.designations.initiativeSeatId
                      ? view.seats[view.designations.initiativeSeatId]?.displayName
                      : 'Unclaimed'}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    onDispatch({
                      type: 'SET_DESIGNATION',
                      designation: view.designations.dayNight === 'day' ? 'night' : 'day',
                    })
                  }
                  className="p-3 rounded-xl bg-slate-900 border border-slate-800 hover:border-slate-700 text-left space-y-1"
                >
                  <div className="font-bold flex items-center gap-1.5">
                    {view.designations.dayNight === 'night' ? (
                      <Moon className="w-4 h-4 text-indigo-300" />
                    ) : (
                      <Sun className="w-4 h-4 text-amber-300" />
                    )}
                    <span>Day / Night</span>
                  </div>
                  <div className="text-[10px] text-slate-400 uppercase font-mono">
                    {view.designations.dayNight ?? 'Inactive'}
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    onDispatch({
                      type: 'SET_DESIGNATION',
                      designation: 'citys_blessing',
                      seatId: view.viewerSeatId,
                    })
                  }
                  className={`p-3 rounded-xl border text-left space-y-1 ${
                    view.viewerSeatId &&
                    view.designations.citysBlessingSeatIds.includes(view.viewerSeatId)
                      ? 'bg-cyan-500/20 border-cyan-400 text-cyan-100'
                      : 'bg-slate-900 border-slate-800 hover:border-slate-700'
                  }`}
                >
                  <div className="font-bold">City&apos;s Blessing</div>
                  <div className="text-[10px] text-slate-400">
                    {view.viewerSeatId &&
                    view.designations.citysBlessingSeatIds.includes(view.viewerSeatId)
                      ? 'Ascended ✓'
                      : 'Click to Ascend'}
                  </div>
                </button>
              </div>

              {/* Undercity Room Tracker */}
              {mySeat && (
                <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">
                      Venture Into The Undercity (Current:{' '}
                      {view.designations.undercityRoomBySeat[mySeat.seatId] ?? 'Not in dungeon'})
                    </span>
                    {view.designations.undercityRoomBySeat[mySeat.seatId] && (
                      <button
                        type="button"
                        onClick={() => onDispatch({ type: 'SET_UNDERCITY_ROOM', room: null })}
                        className="text-[10px] text-rose-300 hover:underline"
                      >
                        Complete / Reset Dungeon
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
                    {UNDERCITY_ROOMS.map((rm) => {
                      const active = view.designations.undercityRoomBySeat[mySeat.seatId] === rm.name;
                      return (
                        <button
                          key={rm.id}
                          type="button"
                          onClick={() => onDispatch({ type: 'SET_UNDERCITY_ROOM', room: rm.name })}
                          className={`p-2 rounded-lg border text-left ${
                            active
                              ? 'bg-emerald-950 border-emerald-400 text-emerald-100'
                              : 'bg-slate-950 border-slate-800 hover:border-slate-700'
                          }`}
                        >
                          <div className="font-bold text-[11px]">
                            T{rm.tier} · {rm.name}
                          </div>
                          <div className="text-[10px] text-slate-400 line-clamp-2">{rm.effect}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* The Ring Tempts You (Levels 1-4 + Ring-bearer) */}
              {mySeat && (
                <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">
                      The Ring Tempts You — Level{' '}
                      {view.designations.ringTemptCountBySeat[mySeat.seatId] ?? 0} / 4
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() =>
                          onDispatch({
                            type: 'SET_RING_BEARER',
                            instanceId: view.designations.ringBearerBySeat[mySeat.seatId] ?? null,
                            temptDelta: -1,
                          })
                        }
                        className="px-2 py-0.5 rounded bg-slate-950 border border-slate-700"
                      >
                        −1 Level
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          onDispatch({
                            type: 'SET_RING_BEARER',
                            instanceId: view.designations.ringBearerBySeat[mySeat.seatId] ?? null,
                            temptDelta: 1,
                          })
                        }
                        className="px-2.5 py-0.5 rounded bg-amber-500 text-slate-950 font-bold"
                      >
                        + Tempt Me
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1">
                    {RING_TEMPT_LEVELS.map((rule, idx) => {
                      const unlocked = (view.designations.ringTemptCountBySeat[mySeat.seatId] ?? 0) >= idx + 1;
                      return (
                        <div
                          key={rule}
                          className={`px-2.5 py-1 rounded text-[11px] ${
                            unlocked
                              ? 'bg-amber-500/15 border border-amber-500/40 text-amber-100'
                              : 'bg-slate-950 text-slate-500'
                          }`}
                        >
                          {rule}
                        </div>
                      );
                    })}
                  </div>

                  <div className="space-y-1">
                    <div className="text-[10px] font-bold uppercase text-slate-400">
                      Choose Ring-Bearer (Your Creatures)
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {mySeat.zones.battlefield
                        .filter((c) => c.row !== 'lands')
                        .map((perm) => {
                          const isBearer =
                            view.designations.ringBearerBySeat[mySeat.seatId] === perm.instanceId;
                          return (
                            <button
                              key={perm.instanceId}
                              type="button"
                              onClick={() =>
                                onDispatch({
                                  type: 'SET_RING_BEARER',
                                  instanceId: isBearer ? null : perm.instanceId,
                                  temptDelta: isBearer ? 0 : 1,
                                })
                              }
                              className={`px-2.5 py-1 rounded-lg border font-semibold ${
                                isBearer
                                  ? 'bg-amber-500 text-slate-950 border-amber-300 font-extrabold'
                                  : 'bg-slate-950 border-slate-700 text-slate-200'
                              }`}
                            >
                              {isBearer ? '💍 ' : ''}
                              {perm.card?.name ?? 'Face-down Creature'}
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ============================================================== */}
          {/* TAB 5: CASCADE / DISCOVER / REVEAL-UNTIL & DICE / RANDOM       */}
          {/* ============================================================== */}
          {tab === 'cascade' && (
            <div className="space-y-4">
              {/* Cascade / Discover / Polymorph Helper */}
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5" />
                    <span>Cascade / Discover / &ldquo;Reveal or Search Until&rdquo; Helper</span>
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <label className="space-y-1">
                    <span className="text-[10px] text-slate-400">Max Mana Value (&le; X)</span>
                    <input
                      type="number"
                      min={0}
                      max={20}
                      value={maxCmcInput}
                      onChange={(e) => setMaxCmcInput(e.target.value)}
                      placeholder="Any MV"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="text-[10px] text-slate-400">Type Filter (optional)</span>
                    <input
                      value={cardTypeFilter}
                      onChange={(e) => setCardTypeFilter(e.target.value)}
                      placeholder="e.g. Creature, Artifact, Land"
                      className="w-full px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700"
                    />
                  </label>
                  <div className="flex flex-col justify-end gap-1.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={nonLandOnly}
                        onChange={(e) => setNonLandOnly(e.target.checked)}
                      />
                      <span>Skip Lands (Nonland only)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-indigo-300">
                      <input
                        type="checkbox"
                        checked={privateSearchUntil}
                        onChange={(e) => setPrivateSearchUntil(e.target.checked)}
                      />
                      <span>Private &ldquo;Search Until&rdquo; (Only you see)</span>
                    </label>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      onDispatch({
                        type: 'REVEAL_UNTIL',
                        maxCmc: maxCmcInput.trim() === '' ? undefined : Number(maxCmcInput),
                        nonLand: nonLandOnly,
                        cardType: cardTypeFilter.trim() || undefined,
                        privateOnly: privateSearchUntil,
                      })
                    }
                    className="px-3.5 py-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-extrabold"
                  >
                    {privateSearchUntil ? 'Run Private Search-Until' : 'Flip & Reveal From Top'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNonLandOnly(false);
                      setMaxCmcInput('');
                      setCardTypeFilter('Creature');
                    }}
                    className="px-2.5 py-2 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-300"
                  >
                    Preset: Polymorph (Creature)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNonLandOnly(false);
                      setMaxCmcInput('');
                      setCardTypeFilter('Land');
                    }}
                    className="px-2.5 py-2 rounded-xl bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-300"
                  >
                    Preset: Until Land
                  </button>
                </div>

                {/* Active Revealed Resolution Tray */}
                {myRevealedCards.length > 0 && (
                  <div className="p-3 rounded-xl bg-slate-950 border border-amber-500/50 space-y-2.5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-bold text-amber-300">
                        Revealed Pile ({myRevealedCards.length} cards) — Resolve Hit & Bottom Rest:
                      </span>
                      <select
                        value={bottomOrder}
                        onChange={(e) => setBottomOrder(e.target.value as 'random' | 'current')}
                        className="px-2 py-1 rounded bg-slate-900 border border-slate-700 text-[11px]"
                      >
                        <option value="random">Bottom rest in CSPRNG Random Order</option>
                        <option value="current">Bottom rest in Current Order</option>
                      </select>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {myRevealedCards.map((c, idx) => {
                        const isLast = idx === myRevealedCards.length - 1;
                        return (
                          <div
                            key={c.instanceId}
                            className={`p-2 rounded-xl border flex flex-col items-center gap-1.5 ${
                              isLast ? 'bg-amber-500/15 border-amber-400' : 'bg-slate-900 border-slate-800'
                            }`}
                          >
                            <CardVisual item={c} size="xs" />
                            <span className="text-[10px] font-semibold max-w-[90px] truncate">
                              {c.card?.name}
                            </span>
                            <div className="flex flex-wrap gap-1 justify-center">
                              {(
                                [
                                  ['battlefield', 'Play'],
                                  ['stack', 'Stack'],
                                  ['hand', 'Hand'],
                                ] as const
                              ).map(([dest, lbl]) => (
                                <button
                                  key={dest}
                                  type="button"
                                  onClick={() =>
                                    onDispatch({
                                      type: 'RESOLVE_REVEALED_UNTIL',
                                      hitInstanceId: c.instanceId,
                                      hitDestination: dest,
                                      bottomOrder,
                                    })
                                  }
                                  className="px-1.5 py-0.5 rounded bg-amber-500 text-slate-950 font-bold text-[9px]"
                                >
                                  {lbl}
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        onDispatch({
                          type: 'RESOLVE_REVEALED_UNTIL',
                          hitInstanceId: null,
                          hitDestination: 'exile',
                          bottomOrder,
                        })
                      }
                      className="px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold"
                    >
                      Put All {myRevealedCards.length} Revealed Cards on Bottom ({bottomOrder})
                    </button>
                  </div>
                )}
              </div>

              {/* CSPRNG Dice, Coin & Random Target Picker */}
              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2.5">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Server-CSPRNG Dice, Coin Flip & Random Target Picker
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {[4, 6, 8, 10, 12, 20, 100].map((sides) => (
                    <button
                      key={sides}
                      type="button"
                      onClick={() => onDispatch({ type: 'ROLL_DIE', sides })}
                      className="px-3 py-1.5 rounded-lg bg-slate-950 hover:bg-slate-800 border border-slate-700 font-mono font-bold text-amber-300"
                    >
                      d{sides}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => onDispatch({ type: 'FLIP_COIN' })}
                    className="px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/60 font-bold text-amber-200"
                  >
                    Flip Coin
                  </button>
                </div>

                <div className="flex flex-wrap gap-1.5 pt-1">
                  {(
                    [
                      ['opponent', 'Random Opponent'],
                      ['any_player', 'Random Seat'],
                      ['my_permanents', 'Random My Permanent'],
                      ['all_permanents', 'Random Any Permanent'],
                      ['my_hand', 'Random Card in Hand'],
                    ] as const
                  ).map(([scope, label]) => (
                    <button
                      key={scope}
                      type="button"
                      onClick={() => onDispatch({ type: 'PICK_RANDOM_TARGET', scope })}
                      className="px-2.5 py-1.5 rounded-lg bg-indigo-950/60 hover:bg-indigo-900/70 border border-indigo-500/40 text-indigo-100 font-semibold"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ============================================================== */}
          {/* TAB 6: PRIVATE SCRATCHPAD, SHARED NOTES & SAVE-FOR-LATER TRAY  */}
          {/* ============================================================== */}
          {tab === 'notes' && (
            <div className="space-y-4">
              {mySeat && mySeat.privateNotes !== null && (
                <div className="p-3 rounded-xl bg-slate-900/80 border border-indigo-500/40 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-300">
                      Private Scratchpad (Visible ONLY to you — hidden from opponents, spectators & stream)
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        onDispatch({ type: 'UPDATE_PRIVATE_NOTES', notes: privateNotesDraft })
                      }
                      className="px-2.5 py-1 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-slate-950 font-extrabold"
                    >
                      Save Private Notes
                    </button>
                  </div>
                  <textarea
                    rows={3}
                    value={privateNotesDraft}
                    onChange={(e) => setPrivateNotesDraft(e.target.value)}
                    placeholder="Track named cards (Pithing Needle, Cavern of Souls), combo lines, or opponent tells privately..."
                    className="w-full p-2.5 rounded-lg bg-slate-950 border border-slate-800 text-slate-100"
                  />
                </div>
              )}

              <div className="p-3 rounded-xl bg-slate-900/80 border border-slate-800 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    Shared Table Notes (Public to everyone at the table)
                  </span>
                  <button
                    type="button"
                    onClick={() => onDispatch({ type: 'UPDATE_SHARED_NOTES', notes: sharedNotesDraft })}
                    className="px-2.5 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold"
                  >
                    Save Table Notes
                  </button>
                </div>
                <textarea
                  rows={2}
                  value={sharedNotesDraft}
                  onChange={(e) => setSharedNotesDraft(e.target.value)}
                  placeholder="Public emblems, named effects, house rule reminders..."
                  className="w-full p-2.5 rounded-lg bg-slate-950 border border-slate-800 text-slate-100"
                />
              </div>

              {/* Save for Later Card Tray */}
              {mySeat && mySeat.savedForLater !== null && (
                <div className="p-3 rounded-xl bg-slate-900/80 border border-amber-500/35 space-y-2.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-300 flex items-center gap-1.5">
                    <Bookmark className="w-3.5 h-3.5" />
                    <span>Private &ldquo;Save for Later&rdquo; Card Tray ({mySeat.savedForLater.length})</span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      value={trayCardInput}
                      onChange={(e) => setTrayCardInput(e.target.value)}
                      placeholder="Bookmark a card name to review/cut/keep after playtesting..."
                      className="flex-1 px-2.5 py-1.5 rounded-lg bg-slate-950 border border-slate-700"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!trayCardInput.trim()) return;
                        onDispatch({ type: 'TOGGLE_SAVE_FOR_LATER', cardName: trayCardInput.trim() });
                        setTrayCardInput('');
                      }}
                      className="px-3 py-1.5 rounded-lg bg-amber-500 text-slate-950 font-bold"
                    >
                      + Save Card
                    </button>
                  </div>
                  {mySeat.savedForLater.length === 0 ? (
                    <p className="text-[11px] text-slate-500">
                      Tip: Inspect any card on the table or in your hand and click &ldquo;Save for Later&rdquo; to bookmark it privately here.
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {mySeat.savedForLater.map((name) => (
                        <span
                          key={name}
                          className="px-2.5 py-1 rounded-full bg-slate-950 border border-amber-400/50 text-amber-100 flex items-center gap-1.5"
                        >
                          <span>{name}</span>
                          <button
                            type="button"
                            aria-label={`Remove ${name}`}
                            onClick={() => onDispatch({ type: 'TOGGLE_SAVE_FOR_LATER', cardName: name })}
                            className="text-slate-400 hover:text-rose-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
